import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { toast } from "sonner";
import { Button, Icon } from "../../";
import {
  usePluginConfigCategory,
  usePluginConfigRefetch,
} from "../../../../hooks/usePlugins";
import PluginSettingsModal from "../PluginSettingsModal";
import { usePostHog, useIntercom } from "@finitless/shared/hooks";
import { useTranslation } from "react-i18next";

const DELIVERECT_PLUGIN_URL = "https://integrations.ordering.co/deliverect";
const DELIVERECT_CONFIG_KEY = "plugin_deliverect_integration";

const DELIVERECT_LOGO_URL =
  "https://www.datocms-assets.com/165162/1753464373-deliverect-restaurants-light-logo.svg";

const DeliverectCard = () => {
  const { t } = useTranslation("integrations");
  const { isFeatureEnabled } = usePostHog();
  const { showMessenger } = useIntercom();
  const posConfigureEnabled = isFeatureEnabled("pos_configure_enabled");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const {
    data: configCategory,
    isLoading: isLoadingConfig,
    refetch: refetchConfig,
  } = usePluginConfigCategory(DELIVERECT_CONFIG_KEY);

  const { refetchPluginConfig } = usePluginConfigRefetch();

  const installPluginMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post("/plugins", {
        url: DELIVERECT_PLUGIN_URL,
      });
      return response;
    },
    onSuccess: () => {
      toast.success("Plugin installed successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to install plugin");
    },
  });

  const isPluginInstalled = !!configCategory;
  const isInstalling = installPluginMutation.isPending;

  const handleConfigure = async (e) => {
    e.stopPropagation();

    if (isPluginInstalled) {
      setIsModalOpen(true);
      return;
    }

    try {
      await installPluginMutation.mutateAsync();
      const result = await refetchConfig();
      if (result.data) {
        setIsModalOpen(true);
      }
    } catch (error) {
      toast.error(error.message || "Failed to install plugin");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    refetchPluginConfig(DELIVERECT_CONFIG_KEY);
  };

  return (
    <>
      <div className="ordering-integration-card flex h-full flex-col rounded-lg border border-gray-800 bg-gray-900 p-6 transition-colors hover:bg-gray-800/50">
        {/* Header */}
        <div className="mb-4">
          <div className="mb-3 flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-700 bg-white">
                {!logoError ? (
                  <img
                    src={DELIVERECT_LOGO_URL}
                    alt="Deliverect"
                    className="h-full w-full object-contain p-1"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <span className="text-lg font-bold text-white">D</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="mb-1 text-lg font-semibold text-white">
                  Deliverect
                </h3>
                <p className="text-sm text-gray-400">
                  POS Integration platform
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-auto space-y-2 border-t border-gray-800 pt-4">
          {posConfigureEnabled ? (
            <Button
              type="button"
              onClick={handleConfigure}
              disabled={isInstalling || isLoadingConfig}
              emphasis="solid"
              intent="brand"
              size="touch"
              className="w-full"
            >
              {isInstalling ? (
                <>
                  <Icon name="Loader" className="mr-2 h-4 w-4 animate-spin" />
                  <span>Installing...</span>
                </>
              ) : isLoadingConfig ? (
                <>
                  <Icon name="Loader" className="mr-2 h-4 w-4 animate-spin" />
                  <span>Loading...</span>
                </>
              ) : (
                <span>Configure</span>
              )}
            </Button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                showMessenger();
              }}
              className="flex w-full items-center justify-center rounded-lg bg-gray-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-600"
            >
              <Icon name="MessageCircle" className="mr-2 h-4 w-4" />
              <span>{t("contactUs")}</span>
            </button>
          )}
        </div>
      </div>

      {/* Plugin Settings Modal */}
      <PluginSettingsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        category={configCategory}
        title="Deliverect Settings"
      />
    </>
  );
};

export default DeliverectCard;
